import React, { useEffect, useRef, useState } from 'react';
import { devError } from '../lib/devLog';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PriceTag from '../components/PriceTag';
import {
  ArrowRight, Camera, Calendar, Flower2, Sparkles, BookOpen, ShieldCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SBM_ARTIST_ID = 'f1e7c91d-3d8c-4e3a-9a7e-6e2b8f0a1234';

const Section = ({ children, className = '', id = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  return (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Display order for the photographer / printmaker groups on the hub.
const GROUP_ORDER = [
  { key: 'Eve Arnold',        kicker: 'Photographer · 1950s sessions, Long Island' },
  { key: 'Milton H. Greene',  kicker: 'Photographer · The Black Sitting, White Robes, Schenck Mansion' },
  { key: 'Douglas Kirkland',  kicker: "Photographer · The 1961 Bedsheet session for Look magazine" },
  { key: 'Sunday B. Morning', kicker: 'After Andy Warhol · Belgian silkscreens from the original negatives' },
  { key: 'David Studwell',    kicker: 'Printmaker · Hand-embellished Diamond Dust screenprints, Sussex' },
  { key: 'Steve Kaufman',     kicker: 'Pop · Hand-embellished silkscreens by Warhol\u2019s former Factory assistant' },
  { key: 'Pure Evil',         kicker: 'Urban · The "I Dream of\u2026" stencil-spray nightmares' },
  { key: 'Andy Warhol',       kicker: 'Pop Art · Factory Editions' },
  { key: 'Invader',           kicker: 'Street Art · Mosaic Marilyn' },
];

const cleanTitle = (title = '') => title
  .replace(' (after Andy Warhol)', '')
  .replace(' — Limited Edition Print', '')
  .replace(' — Open Edition Print', '')
  .replace(/\s+—\s+(Limited|Open) Edition.*$/i, '')
  .trim();

const ArtworkCard = ({ work, navigate, index }) => (
  <motion.button
    type="button"
    onClick={() => navigate(`/artwork/${work.slug || work.id}`)}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.4) }}
    whileHover={{ y: -4 }}
    className="group text-left w-full"
    data-testid={`mm-artwork-card-${work.id}`}
  >
    <div className="aspect-square mb-4 p-[3%] bg-neutral-950 shadow-[0_18px_36px_-12px_rgba(0,0,0,0.4),0_4px_12px_-6px_rgba(0,0,0,0.3)] transition-shadow duration-500 group-hover:shadow-[0_28px_50px_-16px_rgba(0,0,0,0.5),0_8px_18px_-8px_rgba(0,0,0,0.35)]">
      <div className="w-full h-full bg-[#fdfaf3] p-[6%] overflow-hidden flex items-center justify-center">
        <img
          src={work.imageUrl?.startsWith('http') ? work.imageUrl : `${API_URL}${work.imageUrl}`}
          alt={work.altText || `${work.title} by ${work.artistName}`}
          loading="lazy"
          className="max-w-full max-h-full object-contain group-hover:scale-[1.03] transition-transform duration-700 ease-out"
        />
      </div>
    </div>
    <h3 className="text-base font-medium tracking-tight leading-snug line-clamp-2">
      {cleanTitle(work.title)}
    </h3>
    <div className="mt-1 flex items-baseline justify-between gap-3 text-sm text-neutral-500">
      <span className="truncate">{work.year || work.edition?.split(';')[0] || ''}</span>
      <span className="font-medium text-black whitespace-nowrap">
        <PriceTag priceDisplay={work.priceDisplay} vatScheme={work.vatScheme || 'standard'} />
      </span>
    </div>
  </motion.button>
);

const ArtistGroup = ({ id, title, kicker, works, navigate }) => {
  if (!works || !works.length) return null;
  return (
    <Section id={id} className="py-20 sm:py-28 border-t border-neutral-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12 max-w-3xl">
          <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">{kicker}</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">{title}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {works.map((w, i) => (
            <ArtworkCard key={w.id} work={w} navigate={navigate} index={i} />
          ))}
        </div>
      </div>
    </Section>
  );
};

// Sticky chip nav — same UX as SBM page so the catalogue stays navigable.
const GroupNav = ({ groups }) => {
  const available = GROUP_ORDER.filter((g) => (groups[g.key] || []).length > 0);
  const [activeKey, setActiveKey] = useState(available[0]?.key);
  const stripRef = useRef(null);
  // While we're scroll-jumping after a chip click, ignore observer updates
  // and bail out of the activeKey effect — otherwise every section the
  // smooth-scroll crosses fires the observer and the very-tall Eve Arnold
  // section ends up winning the race, reverting the highlight.
  const programmaticUntilRef = useRef(0);

  useEffect(() => {
    if (!available.length) return undefined;
    const observers = available.map((g) => {
      const slug = g.key.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const el = document.getElementById(`mm-group-${slug}`);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          if (Date.now() < programmaticUntilRef.current) return;
          setActiveKey(g.key);
        },
        { rootMargin: '-40% 0px -55% 0px', threshold: 0 },
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available.length]);

  // Center the active chip in the horizontal strip — using manual scrollLeft
  // rather than scrollIntoView, because scrollIntoView with `block: 'nearest'`
  // on a sticky element can cause the browser to re-scroll the document
  // vertically.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const chip = strip.querySelector(`[data-mm-group-key="${activeKey}"]`);
    if (!chip) return;
    const target = chip.offsetLeft - (strip.clientWidth / 2) + (chip.clientWidth / 2);
    strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [activeKey]);

  const handleJump = (key) => {
    const slug = key.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const el = document.getElementById(`mm-group-${slug}`);
    if (!el) return;
    // Lock the active chip + suppress observer firings for the duration of
    // the smooth scroll (~1.2s is plenty for any reachable section).
    programmaticUntilRef.current = Date.now() + 1200;
    setActiveKey(key);
    const offset = window.innerWidth >= 1024 ? 180 : 120;
    const y = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  if (!available.length) return null;

  return (
    <div
      className="sticky top-[64px] lg:top-[124px] z-30 bg-white/95 backdrop-blur border-y border-neutral-200"
      data-testid="mm-group-nav"
    >
      <div
        ref={stripRef}
        className="max-w-7xl mx-auto flex gap-1 sm:gap-2 overflow-x-auto px-4 sm:px-6 py-3"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {available.map((g) => {
          const isActive = g.key === activeKey;
          return (
            <button
              key={g.key}
              data-mm-group-key={g.key}
              data-testid={`mm-nav-chip-${g.key.replace(/\s+/g, '-').toLowerCase()}`}
              type="button"
              onClick={() => handleJump(g.key)}
              className={`shrink-0 px-3 sm:px-4 py-1.5 text-xs sm:text-[13px] tracking-wide whitespace-nowrap transition-colors duration-200 border ${
                isActive
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-neutral-700 border-neutral-200 hover:border-black hover:text-black'
              }`}
            >
              {g.key}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const MarilynMonroePage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchAll = async () => {
      try {
        // The public /api/artworks endpoint does not support a free-text search
        // across the title, so pull a generous page and filter client-side. The
        // dataset is ~1,200 rows total — tiny over the wire and cached at the
        // edge for 5 min.
        const res = await fetch(`${API_URL}/api/artworks?limit=2000`);
        const data = await res.json();
        const all = Array.isArray(data) ? data : [];
        // Strict subject match — title OR series OR subject mentions Marilyn.
        const re = /marilyn/i;
        const filtered = all.filter((w) => {
          if (re.test(w.title || '')) return true;
          if (re.test(w.series || '')) return true;
          if (re.test(w.subject || '')) return true;
          return false;
        });
        setWorks(filtered);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') devError('Marilyn fetch failed', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    // Mount-only fetch — `API_URL` is a module constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group by artist; sort each group "newest first" so the recently-scraped
  // Iconic photographs surface at the top of their section.
  const groups = works.reduce((acc, w) => {
    const k = w.artistName || 'Other';
    (acc[k] = acc[k] || []).push(w);
    return acc;
  }, {});
  Object.keys(groups).forEach((k) => {
    groups[k].sort((a, b) => {
      const stamp = (w) => w.createdAt || w.updatedAt || '';
      const diff = (stamp(b) || '').localeCompare(stamp(a) || '');
      return diff !== 0 ? diff : (b.id || '').localeCompare(a.id || '');
    });
  });

  // Any artist groups not in our curated order get appended alphabetically at
  // the end (e.g. an admin adds a new Marilyn artist later).
  const knownKeys = new Set(GROUP_ORDER.map((g) => g.key));
  const extras = Object.keys(groups).filter((k) => !knownKeys.has(k)).sort();
  const allGroupsForNav = { ...groups };

  // Pick an iconic hero image — prefer Eve Arnold (most cinematic) then SBM
  // Diamond Dust then any.
  const heroSrc = groups['Eve Arnold']?.[0]?.imageUrl
    || works.find((w) => /diamond dust marilyn/i.test(w.title || ''))?.imageUrl
    || works.find((w) => w.artistId === SBM_ARTIST_ID)?.imageUrl
    || works[0]?.imageUrl;
  const heroImage = heroSrc?.startsWith('http') ? heroSrc : (heroSrc ? `${API_URL}${heroSrc}` : null);

  const totalCount = works.length;

  return (
    <div className="min-h-screen bg-white" data-testid="marilyn-monroe-page">
      <SEO
        title="Marilyn at 100 — Centenary Exhibition | Zebra One Gallery"
        description="A two-week centenary exhibition celebrating Marilyn Monroe's 100th birthday. Original photographs by Eve Arnold, Milton H. Greene, Douglas Kirkland and Bert Stern; Diamond Dust silkscreens by David Studwell; and Sunday B. Morning editions after Andy Warhol — all available to view and to buy at Zebra One Gallery in Hampstead."
        canonical={typeof window !== 'undefined' ? window.location.origin + '/marilyn-monroe' : undefined}
        image={heroImage}
      />
      <Header />

      {/* ─────────────────────────── Hero ─────────────────────────── */}
      <motion.section
        ref={heroRef}
        style={{ opacity, scale }}
        className="relative min-h-[88vh] flex items-center justify-center overflow-hidden border-b border-neutral-200 bg-black"
      >
        {heroImage && (
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Marilyn Monroe — Centenary Exhibition at Zebra One Gallery"
              className="w-full h-full object-cover opacity-55"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/85" />
          </div>
        )}

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="space-y-8"
          >
            <div
              className="inline-block px-4 py-2 border border-white/40 text-white text-xs tracking-[0.3em] uppercase backdrop-blur-sm bg-white/10"
              data-testid="mm-hero-eyebrow"
            >
              Centenary Exhibition · 30 May – 14 June 2026
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tight text-white">
              Marilyn at <span className="italic">100</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto font-light leading-relaxed">
              Norma Jeane Mortenson would have turned one hundred this year. Over two weeks in
              Hampstead, we bring together the gallery's full Marilyn collection — photography,
              silkscreens and Pop iconography from the artists who saw her best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => document.getElementById('mm-collection')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black hover:bg-white/90 transition-colors text-sm font-medium tracking-wider"
                data-testid="mm-hero-browse-cta"
              >
                <Sparkles className="w-4 h-4" />
                Browse the Collection
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => document.getElementById('mm-life')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-black transition-colors text-sm font-medium tracking-wider backdrop-blur-sm"
                data-testid="mm-hero-story-cta"
              >
                <BookOpen className="w-4 h-4" />
                Her Life
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* ─────────────────────────── Events strip ─────────────────────────── */}
      <Section className="py-12 sm:py-16 bg-neutral-950 text-white border-b border-neutral-900">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 text-center md:text-left">
          <div className="flex md:items-start gap-4 flex-col md:flex-row items-center" data-testid="mm-event-opening">
            <Calendar className="w-6 h-6 mt-1 text-white/60 shrink-0" />
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-1">Opening Day</p>
              <p className="text-lg leading-snug">Saturday 30 May · 2 – 4 pm</p>
              <p className="text-sm text-white/70 mt-1">Espresso by Fatboy Slim's <em>Peppito Coffee</em> &amp; cake.</p>
            </div>
          </div>
          <div className="flex md:items-start gap-4 flex-col md:flex-row items-center" data-testid="mm-event-sunday">
            <Sparkles className="w-6 h-6 mt-1 text-white/60 shrink-0" />
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-1">Sunday Reception</p>
              <p className="text-lg leading-snug">Sunday 31 May · 2 – 5 pm</p>
              <p className="text-sm text-white/70 mt-1">A glass of <em>Sipsmith Gin</em> with Gabrielle &amp; the team.</p>
            </div>
          </div>
          <div className="flex md:items-start gap-4 flex-col md:flex-row items-center" data-testid="mm-event-run">
            <Flower2 className="w-6 h-6 mt-1 text-white/60 shrink-0" />
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-1">Exhibition</p>
              <p className="text-lg leading-snug">30 May – 14 June 2026</p>
              <p className="text-sm text-white/70 mt-1">At the gallery, 1 Perrins Court, Hampstead NW3.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── Storyline ─────────────────────────── */}
      <Section id="mm-life" className="py-24 sm:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 prose prose-neutral prose-lg">
          <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">A Life in Pictures</div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight !mt-0 mb-10">
            One hundred years of <em>Norma Jeane.</em>
          </h2>
          <p className="text-lg text-neutral-700 leading-relaxed">
            Born <strong>Norma Jeane Mortenson</strong> on 1 June 1926 in Los Angeles, Marilyn's
            beginnings were anything but the gilded story the world would later attach to her. She
            grew up between foster homes and an orphanage. By sixteen she had married a neighbour
            to avoid being returned to the system. A factory floor and a wartime photographer's
            lens, almost by accident, are what changed her life.
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            By the early 1950s she was <em>Marilyn Monroe</em> — the platinum hair, the breath, the
            small mole — and Hollywood had a star it didn't quite know what to do with. She made
            <em> Gentlemen Prefer Blondes</em>, <em>How to Marry a Millionaire</em>, <em>The Seven
            Year Itch</em>, <em>Bus Stop</em>, <em>Some Like It Hot</em>, <em>The Misfits</em>. She
            founded her own production company — extraordinarily bold for a woman in 1950s
            Hollywood — and studied with Lee Strasberg at the Actors Studio when the industry was
            telling her she was a face, not an actress.
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            She was also, almost always, photographed. By <strong>Eve Arnold</strong> on the dunes
            of Long Island in 1955, reading <em>Ulysses</em>. By <strong>Milton H. Greene</strong>
            {' '}in white robes at the Schenck mansion. By <strong>Douglas Kirkland</strong> the
            night she wore nothing but a white silk sheet for <em>Look</em> magazine. By
            <strong> Bert Stern</strong>, days before her death, in what became known as <em>The
            Last Sitting</em>. The photographers loved her because she was generous to the camera
            in a way few of her peers were — playful, melancholy, intelligent, completely awake.
          </p>
        </div>
      </Section>

      {/* ─────────────────────────── Untimely End ─────────────────────────── */}
      <Section className="py-24 sm:py-32 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-3xl mx-auto px-6 prose prose-neutral prose-lg">
          <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">Her Untimely End</div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight !mt-0 mb-10">
            <em>August 1962.</em>
          </h2>
          <p className="text-lg text-neutral-700 leading-relaxed">
            On <strong>4 August 1962</strong>, at thirty-six years old, Marilyn Monroe was found
            dead at her home in Brentwood. The coroner ruled it a probable suicide by barbiturate
            overdose. The decades since have brought theories and counter-theories, biographies and
            films, but the simpler, sadder truth is that she had been unwell for a long time, and
            the industry that had built her image had given her precious little to lean on.
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            What she left was an iconography. <strong>Andy Warhol's</strong> <em>Marilyn</em>
            {' '}silkscreens, made the month after her death, transformed her into the defining
            visual subject of Pop Art. Sixty years on, her image is one of the most reproduced —
            and most argued over — in the history of photography and printmaking.
          </p>
          <blockquote className="border-l-2 border-black not-italic pl-6 my-12">
            <p className="text-2xl font-light leading-snug text-neutral-900 italic">
              "I am not interested in money. I just want to be wonderful."
            </p>
            <footer className="text-sm text-neutral-500 mt-3 not-italic">— Marilyn Monroe</footer>
          </blockquote>

          <div className="not-prose grid grid-cols-1 sm:grid-cols-3 gap-8 mt-12 mb-4">
            <div className="border-l-2 border-black pl-5">
              <Camera className="w-6 h-6 mb-3" />
              <p className="font-medium mb-1">The Photographers</p>
              <p className="text-sm text-neutral-600 leading-relaxed">Arnold, Greene, Kirkland, Stern. The images that made her unforgettable.</p>
            </div>
            <div className="border-l-2 border-black pl-5">
              <Sparkles className="w-6 h-6 mb-3" />
              <p className="font-medium mb-1">The Printmakers</p>
              <p className="text-sm text-neutral-600 leading-relaxed">Sunday B. Morning's Belgian silkscreens; David Studwell's Diamond Dust series.</p>
            </div>
            <div className="border-l-2 border-black pl-5">
              <Flower2 className="w-6 h-6 mb-3" />
              <p className="font-medium mb-1">The Legacy</p>
              <p className="text-sm text-neutral-600 leading-relaxed">A century on, the most reproduced face in the history of the camera and the silkscreen.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── Catalogue ─────────────────────────── */}
      <div id="mm-collection" />
      {loading ? (
        <Section className="py-32 text-center text-neutral-500" data-testid="mm-loading">
          <div className="inline-block w-8 h-8 border-2 border-neutral-300 border-t-black rounded-full animate-spin" />
          <p className="mt-4 text-sm">Loading the collection…</p>
        </Section>
      ) : (
        <>
          <Section className="pt-24 sm:pt-28 pb-4">
            <div className="max-w-7xl mx-auto px-6 max-w-3xl">
              <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">The Collection</div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">
                {totalCount} works, one woman.
              </h2>
              <p className="mt-5 text-neutral-600 leading-relaxed">
                Every Marilyn Monroe work at Zebra One Gallery, gathered in one place for the
                centenary. Click any piece to see edition details and price.
              </p>
            </div>
          </Section>

          <GroupNav groups={allGroupsForNav} />
          {GROUP_ORDER.map((g) => {
            const slug = g.key.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            return (
              <ArtistGroup
                key={g.key}
                id={`mm-group-${slug}`}
                title={g.key}
                kicker={g.kicker}
                works={groups[g.key] || []}
                navigate={navigate}
              />
            );
          })}
          {extras.map((k) => {
            const slug = k.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            return (
              <ArtistGroup
                key={k}
                id={`mm-group-${slug}`}
                title={k}
                kicker="Marilyn Monroe at Zebra One Gallery"
                works={groups[k] || []}
                navigate={navigate}
              />
            );
          })}
        </>
      )}

      {/* ─────────────────────────── CTA ─────────────────────────── */}
      <Section className="py-24 sm:py-32 bg-black text-white border-t border-neutral-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-6 text-white/70" />
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-6">
            Come and see her at the gallery.
          </h2>
          <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto mb-10">
            The exhibition runs <strong>30 May – 14 June 2026</strong> at 1 Perrins Court, Hampstead.
            Saturday opening 2 – 4 pm with Peppito Coffee &amp; cake. Sunday 2 – 5 pm with Sipsmith Gin.
            No booking required for the weekend events.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => navigate('/contact?subject=Marilyn%20at%20100%20Enquiry')}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-black hover:bg-neutral-100 transition-colors text-sm uppercase tracking-[0.2em] font-medium"
              data-testid="mm-cta-enquire"
            >
              Make an Enquiry
              <ArrowRight className="w-5 h-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => navigate('/news/news-marilyn-at-100-2026')}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-white/60 text-white hover:bg-white hover:text-black transition-colors text-sm uppercase tracking-[0.2em] font-medium"
              data-testid="mm-cta-news"
            >
              Read the Announcement
            </motion.button>
          </div>
        </div>
      </Section>

      <Footer />
    </div>
  );
};

export default MarilynMonroePage;
